import React, { useState } from "react";
import { Alert, Avatar, Button, Copy01 } from "@opendate-app/setlist";

import BlurTextInput from "../BlurTextInput";
import ChecklistCard from "../ChecklistCard";
import { useLandingPage } from "../LandingPageBuilderContainer";
import { SLUG_PATTERN, completedLandingPageSteps } from "../landingPageSteps";
import { TEMPLATES } from "../landingPageTemplates";

const styles = {
  stack: { display: "flex", flexDirection: "column", gap: 24, marginTop: 24 },
  section: { display: "flex", flexDirection: "column", gap: 8 },
  label: { fontSize: 14, fontWeight: 600, color: "var(--sl-text-secondary)" },
  linkRow: { display: "flex", alignItems: "flex-start", gap: 8 },
  grow: { flex: 1, minWidth: 0 },
  card: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: 16,
    border: "1px solid var(--sl-border-secondary)",
    borderRadius: "var(--sl-radius-xl, 12px)",
    background: "var(--sl-bg-secondary)",
  },
  cardTop: { display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--sl-text-tertiary)" },
  cardTitle: { fontSize: 14, fontWeight: 600, color: "var(--sl-text-primary)" },
  cardBody: { fontSize: 13, color: "var(--sl-text-tertiary)" },
};

const slugify = (text) => text.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "");
const initialsFor = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0].toUpperCase()).join("");

const signupsSummary = (settings) => {
  const target = settings.after === "redirect" ? "sends to a website" : "thank-you page";
  const segments = (settings.segment_ids || []).length + (settings.create_segment ? 1 : 0);
  return `${segments} ${segments === 1 ? "segment" : "segments"} · ${target}`;
};

const PublishStep = ({ setStep }) => {
  const { page, props, savePage, unpublish, lastErrors } = useLandingPage();
  const [copied, setCopied] = useState(false);
  const settings = page.settings || {};
  const done = completedLandingPageSteps(page);
  const slugOk = SLUG_PATTERN.test(page.slug || "");
  const published = page.status === "published";
  const path = page.public_path || `/p/${page.slug}`;
  const url = `${props.publicBaseUrl}${path}`;
  const venue = props.venueOwnerships.find((item) => item.id === page.venue_ownership_id);
  const venueName = venue?.name || page.venue_name || "";
  const slugTaken = lastErrors.some((message) => message.includes("Slug has already been taken"));

  const checks = [
    {
      step: "template",
      label: "Blueprint",
      ok: done.template,
      text: done.template ? TEMPLATES.find((template) => template.key === page.template_key)?.name || "Custom page" : "Pick a starting point",
    },
    {
      step: "content",
      label: "Content",
      ok: done.content,
      text: done.content ? settings.headline : "Add a headline and button label",
    },
    {
      step: "signups",
      label: "Signups",
      ok: done.signups,
      text: done.signups ? signupsSummary(settings) : "Choose a segment and what happens after signup",
    },
    {
      step: "publish",
      label: "Page link",
      ok: slugOk,
      text: slugOk ? path : "Use at least 3 letters, numbers or dashes",
    },
  ];

  const copy = () => {
    navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div style={styles.stack}>
      {published && <Alert color="success" title="Your page is live" description={url} />}

      <div style={styles.section}>
        <div style={styles.label}>Page link</div>
        <div style={styles.linkRow}>
          <div style={styles.grow}>
            <BlurTextInput
              aria-label="Page link"
              leadingText="/p/"
              value={page.slug}
              transform={slugify}
              error={slugTaken ? "That link is taken" : undefined}
              onSave={(slug) => savePage({ slug })}
            />
          </div>
          <Button variant="secondary" icon={Copy01} disabled={!slugOk} onClick={copy}>
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.label}>How it looks when shared</div>
        <div style={styles.card}>
          <div style={styles.cardTop}>
            <Avatar size="xs" src={venue?.logoUrl || undefined} initials={initialsFor(venueName)} alt={venueName} />
            <span>{new URL(props.publicBaseUrl).host}</span>
          </div>
          <div style={styles.cardTitle}>{[settings.headline, venueName].filter(Boolean).join(" · ")}</div>
          <div style={styles.cardBody}>{settings.subhead}</div>
        </div>
      </div>

      <ChecklistCard checks={checks} onEdit={setStep} />

      {published && (
        <div>
          <Button variant="secondary" onClick={unpublish}>Unpublish</Button>
        </div>
      )}
    </div>
  );
};

export default PublishStep;
